/**
 * Apollo Client setup.
 *
 * Ссылки:
 *   - httpLink     — GraphQL HTTP на gateway
 *   - wsLink       — subscriptions через graphql-ws
 *   - authLink     — Authorization: Bearer <accessToken> + language
 *   - errorLink    — на 401/UNAUTHENTICATED → silent refresh + retry once,
 *                    при failed refresh → signOut() + redirect /app/auth
 *
 * Refresh flow (как в iOS KOService.swift + AuthService.RefreshTokensQuery):
 *   errorLink перехватывает UNAUTHENTICATED, дожидается refresh, повторяет
 *   запрос с новым access-токеном.
 */

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  split,
  fromPromise,
  Observable,
} from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient as createWsClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { gql } from '@apollo/client';
import { Sentry } from '@shared/services/sentry';
import { useAuthStore, getStoredRefreshToken } from '@app/store/auth.store';

let sessionEpoch = 0;
let authSocket: WebSocket | undefined;

const GATEWAY_HTTP =
  import.meta.env.VITE_GATEWAY_HTTP ?? 'https://dev-api.aura-vibe.site/graphql';
const GATEWAY_WS =
  import.meta.env.VITE_GATEWAY_WS ?? 'wss://dev-api.aura-vibe.site/graphql';

const REFRESH_MUTATION = gql`
  mutation WebRefreshTokens($refreshToken: String!) {
    refreshTokens(refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;

/** Внутренний мьютекс чтобы при параллельных 401 выполнять refresh только один раз. */
let refreshPromise: Promise<string | null> | null = null;

/** Импортируем лениво чтобы избежать циклической зависимости на моменте init. */
async function doRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getStoredRefreshToken();
  const epoch = sessionEpoch;
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      const raw = await fetch(GATEWAY_HTTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', language: 'ru' },
        body: JSON.stringify({
          query: REFRESH_MUTATION.loc?.source.body,
          variables: { refreshToken },
        }),
      }).then((r) => r.json());

      const tokens = raw?.data?.refreshTokens;
      if (!tokens?.accessToken) return null;
      if (sessionEpoch !== epoch) return null;
      useAuthStore
        .getState()
        .refreshAccessToken(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken as string;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const httpLink = new HttpLink({
  uri: GATEWAY_HTTP,
  credentials: 'include',
});

const wsLink = new GraphQLWsLink(
  createWsClient({
    url: GATEWAY_WS,
    connectionParams: () => {
      const token = (window as unknown as { __authToken?: string }).__authToken;
      return token ? { authorization: `Bearer ${token}` } : {};
    },
    retryAttempts: Infinity,
    retryWait: async (retries) => {
      const cap = Math.min(30_000, 1000 * 2 ** Math.min(retries, 20));
      await new Promise((resolve) =>
        setTimeout(resolve, cap / 2 + (Math.random() * cap) / 2)
      );
    },
    shouldRetry: () => true,
    on: {
      opened: (socket) => {
        authSocket = socket as WebSocket;
      },
      connected: () => window.dispatchEvent(new Event('aura:ws-connected')),
    },
  })
);

const transportLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink
);

const timeoutLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  if (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  )
    return forward(operation);
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (value) => observer.next(value),
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    });
    const timer = setTimeout(() => {
      observer.error(new Error('Request timed out'));
      subscription.unsubscribe();
    }, operation.getContext().timeoutMs ?? 30000);
    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  });
});

const authLink = new ApolloLink((operation, forward) => {
  const token = (window as unknown as { __authToken?: string }).__authToken;
  let lang = 'ru';
  try {
    lang = localStorage.getItem('aura_lang') ?? 'ru';
  } catch {
    /* Значение по умолчанию. */
  }
  const headers: Record<string, string> = { language: lang };
  if (token) headers.authorization = `Bearer ${token}`;
  operation.setContext({ headers });
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isUnauthenticated = (graphQLErrors ?? []).some(
    (e) => e.extensions?.code === 'UNAUTHENTICATED' || e.message === 'Unauthorized'
  );
  const isNetwork401 =
    networkError &&
    typeof networkError === 'object' &&
    'statusCode' in networkError &&
    (networkError as { statusCode: number }).statusCode === 401;

  if (isUnauthenticated || isNetwork401) {
    return fromPromise(
      doRefresh().then((newToken) => {
        if (operation.getContext().sessionEpoch !== sessionEpoch)
          throw new Error('Account changed');
        if (!newToken) {
          // refresh не удался — логаут
          {
            useAuthStore.getState().signOut();
            if (
              window.location.pathname.startsWith('/app') &&
              !window.location.pathname.includes('/app/auth')
            ) {
              window.location.assign('/app/auth');
            }
          }
          throw new Error('Session expired');
        }
        const oldHeaders = operation.getContext().headers ?? {};
        operation.setContext({
          headers: { ...oldHeaders, authorization: `Bearer ${newToken}` },
        });
        return newToken;
      })
    ).flatMap(() => forward(operation));
  }

  for (const error of graphQLErrors ?? []) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      level: 'error',
      message: operation.operationName,
      data: { code: error.extensions?.code ?? 'UNKNOWN' },
    });
  }
  if (networkError && !isNetwork401)
    Sentry.addBreadcrumb({
      category: 'graphql',
      level: 'error',
      message: operation.operationName,
      data: { code: 'NETWORK' },
    });
});

const sessionLink = new ApolloLink((operation, forward) => {
  const epoch = sessionEpoch;
  operation.setContext({ sessionEpoch: epoch });
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) =>
        epoch === sessionEpoch
          ? observer.next(result)
          : observer.error(new Error('Account changed')),
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });
});
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([sessionLink, errorLink, authLink, timeoutLink, transportLink]),
  cache: new InMemoryCache({}),
  defaultOptions: {
    query: { fetchPolicy: 'cache-first' },
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
});

// Ответы прежней сессии не должны попадать в кеш следующего аккаунта.
useAuthStore.subscribe((next, previous) => {
  if (
    next.isAuthenticated !== previous.isAuthenticated ||
    (previous.userId !== null && previous.userId !== next.userId)
  ) {
    sessionEpoch++;
    void apolloClient.clearStore().catch(() => {});
    authSocket?.close(4205, 'Account changed');
  }
});
